import React, { useEffect, useRef, useCallback } from 'react';
import { useNode, useEditor } from '@craftjs/core';
import { ROOT_NODE } from '@craftjs/utils';
import ReactDOM from 'react-dom';
import { makeStyles } from '@material-ui/core/styles';
import { Box, IconButton, Tooltip, Typography } from '@material-ui/core';

import DeleteIcon from '@material-ui/icons/Delete';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';

const useStyles = makeStyles((theme) => ({
  toolbar: {
    position: 'fixed',
    display: 'flex',
    alignItems: 'center',
    height: '30px',
    marginTop: '-29px',
    zIndex: 9999,
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    padding: theme.spacing(0, 1),
    borderRadius: '3px',
  },
  nodeName: {
    fontSize: '12px',
    marginRight: theme.spacing(2),
  },
  iconButton: {
    padding: theme.spacing(0.5),
    color: 'white',
  },
  dragHandle: {
    cursor: 'move',
  },
}));

export const RenderNode = ({ render }) => {
  const classes = useStyles();
  const { id } = useNode();

  const { actions, query, isActive, isHover } = useEditor((state, query) => ({
    isActive: query.getEvent('selected').contains(id),
    isHover: query.getEvent('hovered').contains(id),
  }));

  const { dom, name, moveable, deletable, parent, connectors: { drag } } = useNode((node) => ({
    dom: node.dom,
    name: node.data.custom.displayName || node.data.displayName,
    moveable: query.node(node.id).isDraggable(),
    deletable: query.node(node.id).isDeletable(),
    parent: node.data.parent,
    connectors: node.connectors,
  }));

  const toolbarRef = useRef(null);

  useEffect(() => {
    if (dom) {
      if (isActive || isHover) {
        dom.classList.add('component-selected');
      } else {
        dom.classList.remove('component-selected');
      }
    }
  }, [dom, isActive, isHover]);

  const getPos = useCallback((domEl) => {
    const { top, left, bottom } = domEl
      ? domEl.getBoundingClientRect()
      : { top: 0, left: 0, bottom: 0 };
    return {
      top: top > 0 ? top : bottom,
      left: left,
    };
  }, []);

  const scroll = useCallback(() => {
    const { current: currentDOM } = toolbarRef;
    if (!currentDOM || !dom) return;

    const { top, left } = getPos(dom);
    currentDOM.style.top = `${top}px`;
    currentDOM.style.left = `${left}px`;
  }, [dom, getPos]);

  useEffect(() => {
    const craftRenderer = document.querySelector('.craftjs-renderer');
    if (!craftRenderer) return;

    craftRenderer.addEventListener('scroll', scroll);
    return () => {
      craftRenderer.removeEventListener('scroll', scroll);
    };
  }, [scroll]);

  return (
    <>
      {(isHover || isActive) &&
        ReactDOM.createPortal(
          <Box
            ref={toolbarRef}
            className={classes.toolbar}
            style={{
              left: getPos(dom).left,
              top: getPos(dom).top,
            }}
          >
            <Typography variant="caption" className={classes.nodeName}>
              {name}
            </Typography>

            {moveable && (
              <Tooltip title="Mover" placement="bottom">
                <div ref={drag} className={classes.dragHandle}>
                  <DragIndicatorIcon fontSize="small" style={{ color: 'white' }} />
                </div>
              </Tooltip>
            )}

            {id !== ROOT_NODE && (
              <Tooltip title="Seleccionar Padre" placement="bottom">
                <IconButton
                  className={classes.iconButton}
                  onClick={() => actions.selectNode(parent)}
                >
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            {deletable && (
              <Tooltip title="Eliminar" placement="bottom">
                <IconButton
                  className={classes.iconButton}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    actions.delete(id);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>,
          document.body
        )}
      {render}
    </>
  );
};